import { Suspense, useContext, useEffect, useState } from 'react';
import styles from './portfolio-summary.module.scss';
import { Button, Typography, Icon, ICONS_ENUM } from '@sanlam-glacier/app-common/components';
import { useCountUp } from 'hooks/useCountUp';
import { getPortfolioSummary } from 'bff/hooks/reporting/PortfolioSummaryApi';
import { useQuery } from '@tanstack/react-query';
import WidgetLoader from '../../../../atoms/widget-loader/WidgetLoader';
import WidgetError from '../../../../atoms/widget-error/WidgetError';
import { WidgetContext } from 'modules/dashboard/components/organisms/widget/Widget';
import { useSetWidgetQueryStatus } from 'modules/dashboard/types/widgets/widget.hooks';
import { formatRandValue } from 'modules/dashboard/utils/formatting/currencyAndPercentageFormatter';

const PortfolioSummaryWidget = () => {
  const [selectedTab, setSelectedTab] = useState<'portfolio' | 'investments'>('portfolio');
  const context = useContext(WidgetContext);
  const testComponentName = 'portfolioSummaryWidget';

  const { data, isError, refetch, isFetching, isSuccess } = useQuery(
    getPortfolioSummary({ clientId: 1 })
  );

  useEffect(() => {
    if (!context) return;
    context.setReloadHandler(() => () => {
      refetch();
    });
  }, []);

  useSetWidgetQueryStatus({ isFetching, isError, isSuccess, context });

  const growthValue =
    data?.closingBalances && data?.openingBalances
      ? data?.closingBalances - data?.openingBalances
      : 0;
  const animatedCurrentAmount = useCountUp(data?.closingBalances ? data.closingBalances : 0);

  return (
    <Suspense fallback={<WidgetLoader />}>
      {isFetching ? (
        <div className={styles.loaderContainer}>
          <WidgetLoader />
        </div>
      ) : isError ? (
        <WidgetError onRefresh={refetch} isRefreshing={isFetching} />
      ) : (
        <div className={styles.widgetContainer} data-testid={testComponentName}>
          <span className={styles.highlight} />
          <div className={styles.header}>
            <div className={styles.tabButtons}>
              <button
                data-testid={testComponentName + 'Portfolio'}
                className={selectedTab === 'portfolio' ? styles.active : ''}
                onClick={() => setSelectedTab('portfolio')}
              >
                <Typography
                  color={Typography.COLOR.TERTIARY}
                  variant={Typography.VARIANT.B3}
                  weight={Typography.WEIGHT.NORMAL}
                >
                  Total portfolio value
                </Typography>
              </button>
              <button
                data-testid={testComponentName + 'Investments'}
                className={selectedTab === 'investments' ? styles.active : ''}
                onClick={() => setSelectedTab('investments')}
              >
                <Typography
                  color={Typography.COLOR.TERTIARY}
                  variant={Typography.VARIANT.B3}
                  weight={Typography.WEIGHT.NORMAL}
                >
                  Investments
                </Typography>
              </button>
            </div>

            {selectedTab != 'portfolio' && (
              <Typography
                data-testid={testComponentName + 'InvestmentNumber'}
                color={Typography.COLOR.DEFAULT}
                variant={Typography.VARIANT.H2}
                weight={Typography.WEIGHT.MEDIUM}
              >
                {data?.investmentPlans?.length || 0} Investments
              </Typography>
            )}
          </div>

          {selectedTab === 'portfolio' ? (
            <div>
              <Typography
                data-testid={testComponentName + 'TotalValue'}
                className={styles.portfolioAmount}
                color={Typography.COLOR.DEFAULT}
                variant={Typography.VARIANT.H2}
                weight={Typography.WEIGHT.MEDIUM}
              >
                R {animatedCurrentAmount}
              </Typography>

              <div className={styles.details}>
                <div className={styles.row}>
                  <Typography
                    color={Typography.COLOR.TERTIARY}
                    variant={Typography.VARIANT.B3}
                    weight={Typography.WEIGHT.NORMAL}
                  >
                    Opening balances
                  </Typography>
                  <Typography
                    data-testid={testComponentName + 'OpeningBalance'}
                    color={Typography.COLOR.DEFAULT}
                    variant={Typography.VARIANT.B3}
                    weight={Typography.WEIGHT.MEDIUM}
                  >
                    R {formatRandValue(data?.openingBalances ?? 0)}
                  </Typography>
                </div>
                <div className={styles.row}>
                  <Typography
                    color={Typography.COLOR.TERTIARY}
                    variant={Typography.VARIANT.B3}
                    weight={Typography.WEIGHT.NORMAL}
                  >
                    Inflows
                  </Typography>
                  <Typography
                    color={Typography.COLOR.DEFAULT}
                    variant={Typography.VARIANT.B3}
                    weight={Typography.WEIGHT.MEDIUM}
                    data-testid={testComponentName + 'Inflows'}
                  >
                    R {formatRandValue(data?.inflows ?? 0)}
                  </Typography>
                </div>
                <div className={styles.row}>
                  <Typography
                    color={Typography.COLOR.TERTIARY}
                    variant={Typography.VARIANT.B3}
                    weight={Typography.WEIGHT.NORMAL}
                  >
                    Outflows
                  </Typography>
                  <Typography
                    color={Typography.COLOR.DEFAULT}
                    variant={Typography.VARIANT.B3}
                    weight={Typography.WEIGHT.MEDIUM}
                    data-testid={testComponentName + 'Outflows'}
                  >
                    R {formatRandValue(data?.outflows ?? 0)}
                  </Typography>
                </div>
                <div className={styles.row}>
                  <Typography
                    color={Typography.COLOR.TERTIARY}
                    variant={Typography.VARIANT.B3}
                    weight={Typography.WEIGHT.NORMAL}
                  >
                    Closing balances
                  </Typography>
                  <Typography
                    color={Typography.COLOR.DEFAULT}
                    variant={Typography.VARIANT.B3}
                    weight={Typography.WEIGHT.MEDIUM}
                    data-testid={testComponentName + 'ClosingBalances'}
                  >
                    R {formatRandValue(data?.closingBalances ?? 0)}
                  </Typography>
                </div>
                <div className={styles.row}>
                  <Typography
                    color={Typography.COLOR.TERTIARY}
                    variant={Typography.VARIANT.B3}
                    weight={Typography.WEIGHT.NORMAL}
                  >
                    Net Growth
                  </Typography>
                  <Typography
                    color={Typography.COLOR.INHERIT}
                    variant={Typography.VARIANT.B3}
                    weight={Typography.WEIGHT.MEDIUM}
                    className={styles.netGrowth}
                    data-testid={testComponentName + 'NetGrowth'}
                  >
                    + R {formatRandValue(growthValue ?? 0)}
                  </Typography>
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.investmentsContainer}>
              <div className={styles.investmentsTable}>
                <div className={styles.investmentsHeader}>
                  <span data-testid={testComponentName + 'InvestmentPlanHeading'}>
                    Investment plan
                  </span>
                  <span
                    data-testid={testComponentName + 'percentagePortfolioHeading'}
                    className={styles.investmentTextRight}
                  >
                    % of portfolio
                  </span>
                  <span
                    data-testid={testComponentName + 'ClosingBalanceHeading'}
                    className={styles.investmentTextRight}
                  >
                    Closing balance
                  </span>
                </div>

                <div className={styles.investmentsList}>
                  {data?.investmentPlans?.map((inv, idx) => (
                    <div
                      data-testid={testComponentName + 'Investment' + idx}
                      className={styles.investmentItem}
                      key={idx}
                    >
                      <div className={styles.planDetails}>
                        <Typography
                          className={styles.planDetailsText}
                          color={Typography.COLOR.DEFAULT}
                          weight={Typography.WEIGHT.NORMAL}
                          variant={Typography.VARIANT.B4}
                          data-testid={testComponentName + 'PlanNumber'}
                        >
                          {inv.investmentPlanNumber}
                        </Typography>
                        <Typography
                          className={styles.planDetailsText}
                          color={Typography.COLOR.DEFAULT}
                          weight={Typography.WEIGHT.NORMAL}
                          variant={Typography.VARIANT.B4}
                          data-testid={testComponentName + 'PlanName'}
                        >
                          {inv.investmentPlanName}
                        </Typography>
                      </div>
                      <Typography
                        color={Typography.COLOR.DEFAULT}
                        weight={Typography.WEIGHT.NORMAL}
                        variant={Typography.VARIANT.B4}
                        data-testid={testComponentName + 'PortfolioPercentage'}
                      >
                        {inv.portfolioPercentage.toFixed(2)}%
                      </Typography>
                      <Typography
                        color={Typography.COLOR.DEFAULT}
                        weight={Typography.WEIGHT.NORMAL}
                        variant={Typography.VARIANT.B4}
                        data-testid={testComponentName + 'ClosingBalance'}
                      >
                        R{' '}
                        {formatRandValue(
                          inv.investmentFunds.reduce((sum, fund) => sum + fund.fundMarketValue, 0)
                        )}
                      </Typography>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <Button
            className={styles.seeSummaryBtn}
            //onClick={() => setFormVisible(true)}
            data-testid={testComponentName + 'SeeFullSummary'}
            variant={Button.VARIANT.SECONDARY}
            size={Button.SIZE.SMALL}
            endIcon={<Icon icon={ICONS_ENUM.NAVIGATION_ARROW_FORWARD} />}
          >
            See full summary
          </Button>
        </div>
      )}
    </Suspense>
  );
};

export default PortfolioSummaryWidget;
